import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Tags,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  adminGroupLabel,
  groupDefinitionsByAdminGroup,
  mapDefinitionRow,
  sortDefinitions,
  type SubscriberFieldDefinitionRow,
} from '../../lib/subscriberCustomFields';
import SubscriberFieldDefinitionModal from './SubscriberFieldDefinitionModal';

function DefinitionTable({
  rows,
  onEdit,
}: {
  rows: SubscriberFieldDefinitionRow[];
  onEdit: (row: SubscriberFieldDefinitionRow) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-3">Label</th>
            <th className="px-4 py-3">Key</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Weight</th>
            <th className="px-4 py-3">Flags</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 w-12" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row.id} className="bg-white dark:bg-gray-800">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900 dark:text-gray-100">{row.label}</div>
                {(row.ui.tags?.length ?? 0) > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {row.ui.tags!.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">{row.key}</td>
              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{row.field_type}</td>
              <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">
                {row.score_points > 0 ? `+${row.score_points}` : '—'}
              </td>
              <td className="px-4 py-3 text-xs text-gray-500">
                {[
                  row.ui.admin_only && 'admin',
                  row.ui.show_on_detail === false && 'hidden',
                  row.ui.required && 'required',
                  row.extraction_prompt && 'extract',
                ]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                    row.active
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {row.active ? 'active' : 'inactive'}
                </span>
              </td>
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onEdit(row)}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title="Edit"
                  aria-label="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SubscriberFieldDefinitionsView() {
  const [rows, setRows] = useState<SubscriberFieldDefinitionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SubscriberFieldDefinitionRow | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriber_field_definitions')
        .select('*')
        .order('key', { ascending: true });
      if (error) throw error;
      setRows((data ?? []).map((r) => mapDefinitionRow(r as Record<string, unknown>)));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load field definitions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortDefinitions(rows);
    return sortDefinitions(
      rows.filter(
        (r) =>
          r.key.toLowerCase().includes(q) ||
          r.label.toLowerCase().includes(q) ||
          r.field_type.toLowerCase().includes(q) ||
          adminGroupLabel(r).toLowerCase().includes(q),
      ),
    );
  }, [rows, searchQuery]);

  const grouped = useMemo(() => groupDefinitionsByAdminGroup(filtered), [filtered]);

  useEffect(() => {
    setExpandedGroups(new Set(grouped.map((g) => g.group)));
  }, [grouped]);

  const allExpanded =
    grouped.length > 0 && grouped.every((g) => expandedGroups.has(g.group));
  const allCollapsed = grouped.every((g) => !expandedGroups.has(g.group));

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const expandAll = () => setExpandedGroups(new Set(grouped.map((g) => g.group)));
  const collapseAll = () => setExpandedGroups(new Set());

  const openEdit = (row: SubscriberFieldDefinitionRow) => {
    setEditing(row);
    setModalOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Tags className="h-7 w-7 text-emerald-500" />
            Subscriber field definitions
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Schema for custom subscriber data. Values are stored separately and exposed as{' '}
            <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">custom_data</code> on each subscriber.
            Group names are admin-only — they organize this catalog, not public forms.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/subscriber-profile-scoring"
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <BarChart3 className="h-4 w-4" />
            Scoring system
          </Link>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            title="New field"
            aria-label="New field"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search by key, label, type, or group…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
          />
        </div>
        {grouped.length > 1 ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={expandAll}
              disabled={allExpanded}
              className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              disabled={allCollapsed}
              className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Collapse all
            </button>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No field definitions yet.</p>
      ) : grouped.length === 1 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DefinitionTable rows={grouped[0].items} onEdit={openEdit} />
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ group, items }) => {
            const open = expandedGroups.has(group);
            return (
              <section
                key={group}
                className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800"
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors"
                  aria-expanded={open}
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
                  )}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{group}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {items.length} field{items.length === 1 ? '' : 's'}
                  </span>
                </button>
                {open ? <DefinitionTable rows={items} onEdit={openEdit} /> : null}
              </section>
            );
          })}
        </div>
      )}

      <SubscriberFieldDefinitionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        definition={editing}
        onSuccess={() => void load()}
        onDuplicated={(row) => setEditing(row)}
      />
    </div>
  );
}
